import {
  faArrowAltCircleLeft,
  faArrowAltCircleRight,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { memo, useMemo } from 'react'
import { useTheme } from '../../../../theme/hooks'

type Props = {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const VISIBLE_PAGES = 5

const PaginationBase = ({ currentPage, totalPages, onPageChange }: Props) => {
  const { theme } = useTheme()

  const pageNumbers = useMemo(() => {
    const upper = Math.min(
      totalPages,
      Math.ceil(currentPage / VISIBLE_PAGES) * VISIBLE_PAGES,
    )
    const lower = Math.max(1, upper - (VISIBLE_PAGES - 1))
    const out: number[] = []
    for (let i = lower; i <= upper; i++) out.push(i)
    return out
  }, [currentPage, totalPages])

  if (totalPages <= 1) return null

  const navStyle = {
    borderColor: theme.button.secondary.border,
    backgroundColor: theme.button.secondary.bg,
    color: theme.button.secondary.text,
  } as const

  return (
    <div className="mt-4 flex justify-center">
      <nav className="flex items-center space-x-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          style={navStyle}
          onMouseEnter={e => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor =
                theme.button.secondary.bgHover
            }
          }}
          onMouseLeave={e => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = theme.button.secondary.bg
            }
          }}
        >
          <FontAwesomeIcon icon={faArrowAltCircleLeft} className="h-4 w-4" />
        </button>
        {pageNumbers.map(i => {
          const active = currentPage === i
          return (
            <button
              key={i}
              onClick={() => onPageChange(i)}
              className="flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors"
              style={{
                borderColor: active
                  ? theme.button.primary.border
                  : theme.button.secondary.border,
                backgroundColor: active
                  ? theme.button.primary.bg
                  : theme.button.secondary.bg,
                color: active
                  ? theme.button.primary.text
                  : theme.button.secondary.text,
              }}
              onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.backgroundColor =
                    theme.button.secondary.bgHover
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.backgroundColor =
                    theme.button.secondary.bg
                }
              }}
            >
              {i}
            </button>
          )
        })}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          style={navStyle}
          onMouseEnter={e => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor =
                theme.button.secondary.bgHover
            }
          }}
          onMouseLeave={e => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = theme.button.secondary.bg
            }
          }}
        >
          <FontAwesomeIcon icon={faArrowAltCircleRight} className="h-4 w-4" />
        </button>
      </nav>
    </div>
  )
}

export const Pagination = memo(PaginationBase)
